import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FiRefreshCw } from "react-icons/fi";
import { IoMdTrendingDown } from "react-icons/io";
import { nseIndicesOptions } from "../Helper/Helperdata";
import { useExport } from "../../hooks/useExport";
import { useMarketSnapshot } from "../../context/MarketSnapshotContext";
import useLivePrices from "../../hooks/useLivePrices";
import { useIndexSymbols } from "../../context/IndexSymbolContext";
import "./OpenHighLowScreener.css";

const TOLERANCE = 0.001;

// const INDICES = [
//   "NIFTY 50",
//   "NIFTY BANK",
//   "NIFTY IT",
//   "NIFTY NEXT 50",
//   "NIFTY MIDCAP 100",
// ];

const OpenHighScreener = () => {
  const navigate = useNavigate();
  const { exportCSV, exportExcel, exporting } = useExport();
  const [selectedIndex, setSelectedIndex] = useState(
    () => localStorage.getItem("screenerIndex") || "NIFTY 50",
  );
  const [sortKey, setSortKey] = useState("pctChange");
  const [sortDir, setSortDir] = useState("desc");

  const { indexSymbols } = useIndexSymbols();
  const {
    loadDashboardIndexMarket,
    dashboardMarketData,
    resetDashboardMarketData,
    dashboardLoading,
  } = useMarketSnapshot();
  const { prices: livePrices, subscribeSymbols } = useLivePrices();

  useEffect(() => {
    if (!selectedIndex) return;
    localStorage.setItem("screenerIndex", selectedIndex);
    // const symbols = indexSymbols?.[selectedIndex] || [];
    let symbols = [];
    if (selectedIndex === "ALL") {
      // union of every index's symbols, deduped
      const set = new Set();
      Object.values(indexSymbols || {}).forEach((list) => {
        (list || []).forEach((s) => s && set.add(s));
      });
      symbols = [...set];
    } else {
      symbols = indexSymbols?.[selectedIndex] || [];
    }

    if (symbols.length) subscribeSymbols(symbols);
    loadDashboardIndexMarket({ indexName: selectedIndex });
  }, [selectedIndex, indexSymbols]);

  const handleIndexChange = (e) => {
    resetDashboardMarketData();
    setSelectedIndex(e.target.value);
  };

  const handleRefresh = () => {
    loadDashboardIndexMarket({ indexName: selectedIndex });
  };
  console.log("Dashbiard market data", dashboardMarketData);
  // Build stocks from dashboard + live
  const allStocks = useMemo(() => {
    const rows = Object.values(dashboardMarketData || {});
    return rows
      .map((item) => {
        const quote = item?.quote?.data;
        const live = livePrices[item?.symbol];
        if (!quote?.ohlc) return null;

        const open = live?.open || quote.ohlc.open;
        const high = live?.high || quote.ohlc.high;
        const low = live?.low || quote.ohlc.low;
        const close = item?.prev_close || quote.ohlc.close;
        const ltp = live?.ltp || quote.last_price;
        const volume = live?.volume || quote.volume || 0;
        if (!open || !high || !low || !ltp) return null;

        const pctChange = close
          ? Number((((ltp - close) / close) * 100).toFixed(2))
          : 0;
        const dayPriceChange = close ? Number((ltp - close).toFixed(2)) : 0;
        const isOpenHigh = Math.abs(open - high) / open <= TOLERANCE;

        return {
          symbol: item.symbol,
          securityId: item.security_id,
          open,
          high,
          low,
          close,
          ltp,
          volume,
          pctChange,
          dayPriceChange,
          isOpenHigh,
          exactMatch: open === high,
          dropFromOpen: Number((((open - ltp) / open) * 100).toFixed(2)),
          vwap: item?.intraday?.vwap || item?.vwap || null,
        };
      })
      .filter(Boolean);
  }, [dashboardMarketData, livePrices]);

  const openHighStocks = useMemo(
    () => allStocks.filter((s) => s.isOpenHigh),
    [allStocks],
  );

  // Sort
  const sorted = useMemo(() => {
    return [...openHighStocks].sort((a, b) => {
      const aVal = a[sortKey] ?? 0;
      const bVal = b[sortKey] ?? 0;
      if (typeof aVal === "string")
        return sortDir === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [openHighStocks, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const SortTh = ({ label, field, align }) => (
    <th
      onClick={() => handleSort(field)}
      className={align === "left" ? "ohl__th-left" : ""}
    >
      {label}
      {sortKey === field && (
        <span className="ohl__sort-arrow">
          {sortDir === "asc" ? " ↑" : " ↓"}
        </span>
      )}
    </th>
  );
  const EXPORT_COLUMNS = [
    { header: "Symbol", key: "symbol", width: 15 },
    { header: "Price", key: "ltp", width: 12 },
    { header: "Day Price Change", key: "dayPriceChange", width: 16 },
    { header: "Change %", key: "pctChange", width: 12 },
    { header: "Open", key: "open", width: 12 },
    { header: "High", key: "high", width: 12 },
    { header: "Drop from Open %", key: "dropFromOpen", width: 16 },
    { header: "Volume", key: "volume", width: 14 },
    { header: "Exact Match", key: "exactMatch", width: 12 },
  ];

  const buildExportRows = () =>
    sorted.map((s) => ({
      symbol: s.symbol,
      ltp: s.ltp ?? "",
      dayPriceChange: s.dayPriceChange ?? "",
      pctChange: s.pctChange ?? "",
      open: s.open ?? "",
      high: s.high ?? "",
      dropFromOpen: s.dropFromOpen ?? "",
      volume: s.volume ?? "",
      exactMatch: s.exactMatch ? "EXACT" : "",
    }));

  const handleDownloadCSV = () =>
    exportCSV({
      rows: buildExportRows(),
      columns: EXPORT_COLUMNS,
      filename: `${selectedIndex}_open_equals_high`,
      sheetName: selectedIndex,
    });

  const handleDownloadExcel = () =>
    exportExcel({
      rows: buildExportRows(),
      columns: EXPORT_COLUMNS,
      filename: `${selectedIndex}_open_equals_high`,
      sheetName: selectedIndex,
    });

  const hasData =
    dashboardMarketData && Object.keys(dashboardMarketData).length > 0;

  return (
    <div className="ohl">
      {/* Header */}
      <div className="ohl__header">
        <div className="ohl__header-left">
          <div className="ohl__title-row">
            <button className="ohl__back" onClick={() => navigate(-1)}>
              ←
            </button>
            <h1 className="ohl__title">Open = High Stocks</h1>
          </div>
          <p className="ohl__sub">
            Stocks that opened at the high of the day — sellers dominated from
            market open
          </p>
          <div className="ohl__condition-pill">
            <span>Open Price</span>
            <span className="ohl__condition-eq">=</span>
            <span className="ohl__condition-val ohl__condition-val--bear">
              High Price
            </span>
          </div>
        </div>
        <div className="ohl__header-right">
          <select
            className="ohl__index-select"
            value={selectedIndex}
            onChange={handleIndexChange}
          >
            <option value="ALL">All Stocks</option>
            {nseIndicesOptions?.map((idx) => (
              <option key={idx} value={idx}>
                {idx}
              </option>
            ))}
          </select>
          <button className="ohl__refresh-btn" onClick={handleRefresh}>
            <FiRefreshCw size={14} />
          </button>
          <button
            className="ohl__refresh-btn"
            onClick={handleDownloadCSV}
            disabled={exporting}
          >
            {exporting ? "…" : "CSV ⬇"}
          </button>
          <button
            className="ohl__refresh-btn"
            onClick={handleDownloadExcel}
            disabled={exporting}
          >
            {exporting ? "…" : "Excel ⬇"}
          </button>
        </div>
      </div>

      {/* Result count */}
      <div className="ohl__result-bar">
        <span className="ohl__result-count">
          Showing <b>{sorted.length}</b> of <b>{allStocks.length}</b> stocks in{" "}
          {selectedIndex}
        </span>
        <span className="ohl__live-indicator">
          <span className="ohl__live-dot" /> Live
        </span>
      </div>

      {/* Table */}
      {dashboardLoading && !hasData ? (
        <div className="ohl__loading">Loading {selectedIndex} stocks...</div>
      ) : sorted.length === 0 ? (
        <div className="ohl__empty">
          <div className="ohl__empty-icon">📉</div>
          <div className="ohl__empty-title">
            No Open = High stocks in {selectedIndex}
          </div>
          <div className="ohl__empty-sub">
            This condition develops as the day progresses
          </div>
        </div>
      ) : (
        <div className="ohl__table-wrap">
          <table className="ohl__table">
            <thead>
              <tr>
                <SortTh label="Name" field="symbol" align="left" />
                <SortTh label="Price" field="ltp" />
                <SortTh label="Day Price Change" field="dayPriceChange" />
                <SortTh label="Change %" field="pctChange" />
                <SortTh label="Open" field="open" />
                <SortTh label="Low" field="low" />
                <SortTh label="High" field="high" />
                <SortTh label="Drop from Open" field="dropFromOpen" />
                <SortTh label="Volume" field="volume" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((stock) => (
                <tr
                  key={stock.symbol}
                  className="ohl__row"
                  onClick={() =>
                    navigate(`/stockdetail/${stock.symbol}/${stock.securityId}`)
                  }
                >
                  <td className="ohl__td-name">
                    <span className="ohl__symbol">{stock.symbol}</span>
                    {stock.exactMatch && (
                      <span className="ohl__exact">EXACT</span>
                    )}
                  </td>
                  <td className="ohl__td-num">
                    {stock.ltp?.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td
                    className={`ohl__td-num ${stock.dayPriceChange >= 0 ? "ohl__green" : "ohl__red"}`}
                  >
                    {stock.dayPriceChange > 0 ? "+" : ""}
                    {stock.dayPriceChange?.toFixed(2)}
                  </td>
                  <td
                    className={`ohl__td-num ${stock.pctChange >= 0 ? "ohl__green" : "ohl__red"}`}
                  >
                    {stock.pctChange > 0 ? "+" : ""}
                    {stock.pctChange}%
                  </td>
                  <td className="ohl__td-num">₹{stock.open?.toFixed(2)}</td>
                  <td className="ohl__td-num">₹{stock.low?.toFixed(2)}</td>
                  <td className="ohl__td-num">₹{stock.high?.toFixed(2)}</td>
                  <td className="ohl__red ohl__td-num">
                    {stock.dropFromOpen > 0 ? "-" : ""}
                    {Math.abs(stock.dropFromOpen)}%
                  </td>
                  <td className="ohl__td-num">
                    {stock.volume?.toLocaleString("en-IN")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      {sorted.length > 0 && (
        <div className="ohl__footer">
          <span>
            Showing {sorted.length} of {allStocks.length} results
          </span>
        </div>
      )}
    </div>
  );
};

export default OpenHighScreener;
